import { useState } from "react";
import { Search, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { api, errorMessage } from "@/lib/api";
import { CartLine } from "@/components/CartLine";

const FLOW = ["paid", "sent_to_ton_atelier", "in_production", "shipped", "delivered"];

const Timeline = ({ status }) => {
  const { t } = useLang();
  if (status === "cancelled" || status === "awaiting_payment")
    return <p className="rounded-md bg-white/5 p-3 font-bold" data-testid="track-status">{t(`status.${status}`)}</p>;
  const idx = FLOW.indexOf(status);
  return (
    <ol className="grid gap-3 sm:grid-cols-5" data-testid="track-timeline">
      {FLOW.map((s, i) => (
        <li key={s} className={`flex items-center gap-2 rounded-md border p-3 text-sm font-bold ${i <= idx ? "border-gold/50 bg-gold/10 text-white" : "border-white/10 text-slate-500"}`} data-testid={`track-step-${s}`}>
          <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full ${i <= idx ? "bg-gold text-ink" : "bg-white/10"}`}>{i <= idx && <Check className="h-3 w-3" />}</span>
          {t(`status.${s}`)}
        </li>
      ))}
    </ol>
  );
};

export default function TrackOrder() {
  const { t, money, lang } = useLang();
  const [num, setNum] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders/lookup", { order_number: num, email });
      setOrder(data);
    } catch (err) {
      setOrder(null);
      toast.error(errorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-5 py-14 lg:px-8" data-testid="track-page">
      <h1 className="font-display text-4xl sm:text-5xl">{t("track.title")}</h1>
      <p className="mt-3 text-slate-400">{t("track.sub")}</p>
      <form onSubmit={submit} className="panel mt-8 grid gap-4 p-6 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <label><span className="label">{t("track.order_no")}</span><input value={num} onChange={(e) => setNum(e.target.value)} placeholder="MCI-123456" className="field font-mono-num uppercase" required data-testid="order-lookup-input" /></label>
        <label><span className="label">{t("track.email")}</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="field" required data-testid="order-lookup-email" /></label>
        <button className="btn-gold" disabled={busy} data-testid="order-lookup-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} {t("track.submit")}</button>
      </form>
      {order && (
        <div className="mt-10 flex flex-col gap-6" data-testid="track-result">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <p className="font-mono-num text-2xl font-bold text-gold" data-testid="track-order-number">{order.order_number}</p>
            <p className="text-sm text-slate-400">{t("track.placed", { d: new Date(order.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "long" }) })}</p>
          </div>
          <Timeline status={order.status} />
          {order.tracking_number && (
            <p className="panel p-4 text-sm"><span className="font-bold text-slate-400">{t("track.tracking")}: </span><span className="font-mono-num font-bold" data-testid="track-tracking-number">{order.tracking_number}</span></p>
          )}
          <div className="flex flex-col gap-3">
            {order.items.map((l, i) => <CartLine key={i} line={{ ...l, upload: { original_filename: l.filename }, unit_price: l.line_total / l.quantity }} index={i} />)}
          </div>
          <div className="panel ml-auto w-full max-w-sm p-5 text-sm">
            <div className="flex justify-between py-1 text-slate-300"><span>{t("cart.subtotal")}</span><span className="font-mono-num">{money(order.subtotal)}</span></div>
            <div className="flex justify-between py-1 text-slate-300"><span>{t("cart.shipping")}</span><span className="font-mono-num">{money(order.shipping_cost)}</span></div>
            {order.amount_paid != null && <div className="flex justify-between border-t border-white/10 pt-2 font-extrabold"><span>{t("cart.total")}</span><span className="font-mono-num text-gold" data-testid="track-total">{money(order.amount_paid)}</span></div>}
          </div>
        </div>
      )}
    </div>
  );
}
